// components/tenant-enrollment/sso-configuration.tsx
'use client';

import React, { useState } from 'react';
import { Shield, ArrowRight, ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTheme } from '@/lib/theme-context';
import { getBackgroundClasses, getTextClasses, getBorderClasses, getInputClasses } from '@/lib/theme-utils';

interface SSOConfigurationProps {
  domain: string;
  organizationName: string;
  onComplete: (ssoConfig: any) => void;
  onBack: () => void;
  isLoading?: boolean;
}

type SSOProvider = 'entra' | 'okta' | 'custom' | null;

export function SSOConfiguration({ 
  domain, 
  organizationName, 
  onComplete, 
  onBack, 
  isLoading 
}: SSOConfigurationProps) {
  const { theme } = useTheme();
  const [provider, setProvider] = useState<SSOProvider>(null);
  const [config, setConfig] = useState({
    authority: '',
    client_id: '',
    client_secret: '',
    provider_type: ''
  });
  const [error, setError] = useState<string | null>(null);

  const handleProviderSelect = (selectedProvider: SSOProvider) => {
    setProvider(selectedProvider);
    setError(null);
    
    // Pre-populate configuration based on provider
    if (selectedProvider === 'entra') {
      setConfig({
        authority: `https://login.microsoftonline.com/common`,
        client_id: '',
        client_secret: '',
        provider_type: 'entra'
      });
    } else if (selectedProvider === 'okta') {
      setConfig({
        authority: `https://${domain}.okta.com`,
        client_id: '',
        client_secret: '',
        provider_type: 'okta'
      });
    } else if (selectedProvider === 'custom') {
      setConfig({
        authority: '',
        client_id: '',
        client_secret: '',
        provider_type: 'custom'
      });
    }
  };

  const handleConfigChange = (field: string, value: string) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!provider) {
      setError('Please select an SSO provider');
      return;
    }

    if (!config.authority || !config.client_id) {
      setError('Please fill in all required fields');
      return;
    }

    // Map the provider to the backend enum
    const ssoProviderType = provider === 'entra' ? 'azure' : 
                           provider === 'okta' ? 'okta' : 
                           provider === 'custom' ? 'local' : 'local';

    // Create the SSO config object for the enrollment request
    const ssoConfig = {
      ssoProviderType,
      providerConfig: {
        authority: config.authority,
        clientId: config.client_id,
        clientSecret: config.client_secret,
        providerType: config.provider_type
      }
    };

    onComplete(ssoConfig);
  };

  const providerOptions = [
    {
      id: 'entra',
      name: 'Microsoft Entra ID (Azure AD)',
      description: 'Use Microsoft Entra ID for authentication',
      icon: '🔵',
      popular: true
    },
    {
      id: 'okta',
      name: 'Okta',
      description: 'Use Okta as your identity provider',
      icon: '🟣',
      popular: true
    },
    {
      id: 'custom',
      name: 'Custom SAML/OIDC',
      description: 'Configure a custom SAML or OIDC provider',
      icon: '⚙️',
      popular: false
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <div className="mx-auto w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mb-3">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <h2 className={`text-xl font-bold ${getTextClasses(theme)} mb-1`}>
          Configure Single Sign-On
        </h2>
        <p className={`${getTextClasses(theme, 'muted')} text-sm`}>
          Set up SSO for {organizationName} ({domain})
        </p>
      </div>

      {!provider ? (
        // Provider Selection
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {providerOptions.map((option) => (
              <Card
                key={option.id}
                className={`cursor-pointer transition-all hover:/50 ${
                  provider === option.id ? 'border-cyan-500 bg-cyan-500/10' : `${getBorderClasses(theme)} ${getBackgroundClasses(theme, 'card')}`
                }`}
                onClick={() => handleProviderSelect(option.id as SSOProvider)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-xl">{option.icon}</span>
                    <div>
                      <CardTitle className={`${getTextClasses(theme)} text-base`}>{option.name}</CardTitle>
                      {option.popular && (
                        <span className="text-xs /20  px-2 py-0.5 rounded-full">
                          Popular
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>{option.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={onBack}
              className={`${getBorderClasses(theme)} ${getTextClasses(theme)} h-10`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      ) : (
        // Configuration Form
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className={`${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg p-3`}>
            <div className="flex items-center space-x-2 mb-3">
              <span className="text-xl">
                {providerOptions.find(p => p.id === provider)?.icon}
              </span>
              <div>
                <h3 className={`text-base font-semibold ${getTextClasses(theme)}`}>
                  {providerOptions.find(p => p.id === provider)?.name}
                </h3>
                <p className={`text-xs ${getTextClasses(theme, 'muted')}`}>
                  Configure your {provider} settings
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Authority/Issuer */}
              <div className="space-y-1">
                <Label htmlFor="authority" className={`${getTextClasses(theme)} text-sm`}>
                  Authority/Issuer URL
                </Label>
                <Input
                  id="authority"
                  type="url"
                  value={config.authority}
                  onChange={(e) => handleConfigChange('authority', e.target.value)}
                  placeholder={
                    provider === 'entra' 
                      ? 'https://login.microsoftonline.com/your-tenant-id'
                      : provider === 'okta'
                      ? `https://${domain}.okta.com`
                      : 'https://your-provider.com'
                  }
                  className={`${getInputClasses(theme)} h-10`}
                  disabled={isLoading}
                  required
                />
              </div>

              {/* Client ID */}
              <div className="space-y-1">
                <Label htmlFor="client_id" className={`${getTextClasses(theme)} text-sm`}>
                  Client ID
                </Label>
                <Input
                  id="client_id"
                  type="text"
                  value={config.client_id}
                  onChange={(e) => handleConfigChange('client_id', e.target.value)}
                  placeholder="Enter your client ID"
                  className={`${getInputClasses(theme)} h-10`}
                  disabled={isLoading}
                  required
                />
              </div>

              {/* Client Secret (optional for some providers) */}
              {provider === 'custom' && (
                <div className="space-y-1">
                  <Label htmlFor="client_secret" className={`${getTextClasses(theme)} text-sm`}>
                    Client Secret (Optional)
                  </Label>
                  <Input
                    id="client_secret"
                    type="password"
                    value={config.client_secret}
                    onChange={(e) => handleConfigChange('client_secret', e.target.value)}
                    placeholder="Enter your client secret"
                    className={`${getInputClasses(theme)} h-10`}
                    disabled={isLoading}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="/10 border /30 rounded-lg p-3" style={{ backgroundColor: 'var(--theme-error)' }}>
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4  mr-2" />
                <p className="text-xs ">{error}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => setProvider(null)}
              className={`${getBorderClasses(theme)} ${getTextClasses(theme)} h-10`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Provider Selection
            </Button>

            <Button
              type="submit"
              disabled={!config.authority || !config.client_id || isLoading}
              className=" hover:from-cyan-600 hover:to-blue-700 text-white h-10"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      )}

      {/* Help Text */}
      <div className={`mt-4 p-3 ${getBackgroundClasses(theme, 'card')} ${getBorderClasses(theme)} rounded-lg`}>
        <h4 className={`text-xs font-medium ${getTextClasses(theme)} mb-1`}>Need help configuring SSO?</h4>
        <ul className={`text-xs ${getTextClasses(theme, 'muted')} space-y-0.5`}>
          <li>• Contact your IT administrator for SSO configuration details</li>
          <li>• We'll test the configuration in the next step</li>
          <li>• You can always update these settings later</li>
        </ul>
      </div>
    </div>
  );
}